/*
 * Wishlist hearts on Supabase — window.PkmWishlist.
 *
 * Replaces the `wishlistList` cookie. Hearts are [data-wl-heart] buttons
 * (snippets/wishlist-icon.liquid on cards, snippets/wishlist-product.liquid on
 * the PDP) wired by document-level delegation, so AJAX-rendered cards work
 * without re-binding; refresh() re-applies the saved state after a re-render.
 *
 * Account-only. Signed out (empty data-customer-id on the script tag) a click
 * goes to login and back to this page (the chosen variant, on a PDP). Signed
 * in, the list is fetched once from /a/pkm/wishlist-list and cached for 5
 * minutes in sessionStorage per customer id. That id is UI state only; the App Proxy signs the real one, and nothing
 * here is an identity claim.
 *
 * Plain ES5-ish JS, no jQuery, textContent only — same house style as
 * restock-notify.js and wishlist-center.js.
 */
(function () {
  'use strict';

  if (window.PkmWishlist) return;

  var CACHE_MS = 5 * 60 * 1000;

  var script = document.querySelector('script[data-pkm-wishlist]');
  var cfg = {
    list: (script && script.getAttribute('data-endpoint-list')) || '/a/pkm/wishlist-list',
    add: (script && script.getAttribute('data-endpoint-add')) || '/a/pkm/wishlist-add',
    remove: (script && script.getAttribute('data-endpoint-remove')) || '/a/pkm/wishlist-remove',
    // snippets/login-url.liquid with __RETURN__ where the encoded return path goes.
    loginTemplate: (script && script.getAttribute('data-login-template')) ||
      '/customer_authentication/login?return_to=__RETURN__',
    wishlistUrl: (script && script.getAttribute('data-wishlist-url')) || '/pages/wishlist',
    msgFailed: (script && script.getAttribute('data-msg-failed')) || ''
  };

  var items = null;
  var inflight = null;

  var cid = (script && script.getAttribute('data-customer-id')) || null;

  function cacheKey() {
    return 'pkm:wl:' + cid;
  }

  function readCache() {
    try {
      var raw = window.sessionStorage.getItem(cacheKey());
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (!cached || !cached.items || Date.now() - cached.ts > CACHE_MS) return null;
      return cached.items;
    } catch (e) {
      return null;
    }
  }

  function writeCache() {
    try {
      window.sessionStorage.setItem(cacheKey(), JSON.stringify({ ts: Date.now(), items: items || [] }));
    } catch (e) { /* private mode / quota: the cache is an optimisation only */ }
  }

  function invalidate() {
    items = null;
    try {
      window.sessionStorage.removeItem(cacheKey());
    } catch (e) { /* noop */ }
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        if (!response.ok || !data || data.ok === false) {
          var error = new Error('HTTP ' + response.status);
          error.code = data && data.code;
          throw error;
        }
        return data;
      });
    });
  }

  function list(force) {
    if (!cid) return Promise.resolve([]);
    if (!force) {
      if (items) return Promise.resolve(items);
      var cached = readCache();
      if (cached) {
        items = cached;
        return Promise.resolve(items);
      }
    }
    if (inflight) return inflight;
    inflight = fetch(cfg.list, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        items = (data && data.items) || [];
        writeCache();
        inflight = null;
        return items;
      })
      .catch(function (error) {
        if (window.console && console.warn) console.warn('wishlist list failed', error);
        inflight = null;
        items = items || [];
        return items;
      });
    return inflight;
  }

  function isSaved(handle) {
    if (!items || !handle) return false;
    for (var i = 0; i < items.length; i++) {
      if (items[i].product_handle === handle) return true;
    }
    return false;
  }

  function setHeart(button, saved) {
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    var label = saved ? button.getAttribute('data-label-saved') : button.getAttribute('data-label-add');
    if (label) {
      button.setAttribute('aria-label', label);
      var tip = button.querySelector('.tooltip-label');
      if (tip) tip.textContent = label;
    }
  }

  function refresh() {
    var hearts = document.querySelectorAll('[data-wl-heart]');
    for (var i = 0; i < hearts.length; i++) {
      setHeart(hearts[i], isSaved(hearts[i].getAttribute('data-product-handle')));
    }
  }

  function toast(message) {
    if (!message) return;
    var node = document.createElement('div');
    node.className = 'pkm-wl-toast';
    node.setAttribute('role', 'status');
    node.textContent = message;
    document.body.appendChild(node);
    window.setTimeout(function () {
      if (node.parentNode) node.parentNode.removeChild(node);
    }, 3500);
  }

  function toInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? null : n;
  }

  // PDP hearts carry a variant map and the #ProductSelect id: save the variant
  // on screen. Cards carry the first variant explicitly.
  function dataFromHeart(button) {
    var d = button.dataset;
    var data = {
      sku: d.sku || null,
      variant_id: toInt(d.variantId),
      variant_title: d.variantTitle || null,
      product_id: toInt(d.productId),
      product_handle: d.productHandle || null,
      product_title: d.productTitle || null,
      image_url: d.imageUrl || null
    };
    if (d.variants && d.selectId) {
      var select = document.getElementById(d.selectId);
      var id = select && select.value ? parseInt(select.value, 10) : NaN;
      try {
        var variants = JSON.parse(d.variants);
        for (var i = 0; i < variants.length; i++) {
          if (variants[i].id === id) {
            data.sku = variants[i].sku || data.sku;
            data.variant_id = variants[i].id;
            data.variant_title = variants[i].title;
            break;
          }
        }
      } catch (e) { /* keep the first-variant defaults */ }
    }
    return data;
  }

  // Back to where the shopper clicked. On a PDP the variant picker doesn't
  // touch the URL, so pin the chosen variant or they land on the default one.
  function loginUrlFor(variantId) {
    var loc = window.location;
    var back = loc.pathname + loc.search;
    if (variantId && loc.pathname.indexOf('/products/') !== -1) {
      back = loc.pathname + '?variant=' + variantId;
    }
    return cfg.loginTemplate.replace('__RETURN__', encodeURIComponent(back));
  }

  function add(data) {
    if (!cid) return Promise.reject(new Error('signed_out'));
    return postJSON(cfg.add, data).then(function (result) {
      var saved = {
        id: result.id != null ? String(result.id) : null,
        sku: data.sku,
        variant_id: data.variant_id,
        product_id: data.product_id,
        product_handle: data.product_handle,
        product_title: data.product_title,
        variant_title: data.variant_title,
        image_url: data.image_url,
        created_at: new Date().toISOString()
      };
      items = (items || []).filter(function (item) {
        return item.id !== saved.id && !(data.sku && item.sku === data.sku);
      });
      items.unshift(saved);
      writeCache();
      refresh();
      return result;
    });
  }

  function remove(target) {
    if (!cid) return Promise.reject(new Error('signed_out'));
    var body = target && target.id != null ? { id: String(target.id) } : { sku: target && target.sku };
    return postJSON(cfg.remove, body).then(function (result) {
      items = (items || []).filter(function (item) {
        return body.id ? String(item.id) !== body.id : item.sku !== body.sku;
      });
      writeCache();
      refresh();
      return result;
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var button = target.closest('[data-wl-heart]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    if (!cid) {
      window.location.href = loginUrlFor(dataFromHeart(button).variant_id);
      return;
    }
    // Already saved: the heart is a link to the wishlist, as the cookie heart was.
    if (button.getAttribute('aria-pressed') === 'true') {
      window.location.href = cfg.wishlistUrl;
      return;
    }

    var data = dataFromHeart(button);
    setHeart(button, true);
    button.setAttribute('aria-busy', 'true');
    add(data)
      .catch(function (error) {
        if (window.console && console.warn) console.warn('wishlist add failed', error);
        setHeart(button, false);
        toast(cfg.msgFailed);
      })
      .then(function () {
        button.removeAttribute('aria-busy');
      });
  });

  window.PkmWishlist = {
    refresh: refresh,
    isSaved: isSaved,
    add: add,
    remove: remove,
    list: list,
    invalidate: invalidate
  };

  function boot() {
    if (!cid) return;
    list(false).then(refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', refresh);
})();
